import { useQuery } from '@tanstack/react-query';
import { HelpCircle, Power, Send } from 'lucide-react';
import type { SystemControls } from '@command620/shared';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';
import { Skeleton } from '@/components/ui';

export const useSystemControls = () =>
  useQuery({
    queryKey: ['system', 'controls'],
    queryFn: ({ signal }) => api<SystemControls>('/system/controls', { signal }),
    refetchInterval: 30_000,
  });

/** Always icon + text. LIVE is amber (a risk state); HALTED is red; unknown is neutral (never shown as safe). */
export function OutboundStatus() {
  const { data, isPending, isError } = useSystemControls();
  if (isPending) return <Skeleton className="h-6 w-28" />;
  const [Icon, text, tone] = isError
    ? [HelpCircle, 'unknown', 'border-line text-fg-muted']
    : data.killSwitch
      ? [Power, 'halted', 'border-dnc/50 bg-dnc-bg text-danger-fg']
      : [Send, 'live', 'border-accent/50 bg-active text-fg-accent'];
  return (
    <span
      role="status"
      title={isError ? "Couldn't read the kill-switch state" : undefined}
      className={cn('inline-flex h-6 items-center gap-1.5 rounded-sm border px-2 text-xs font-medium', tone)}
    >
      <Icon className="size-3.5" aria-hidden />
      <span className="font-mono tracking-wide">OUTBOUND</span>
      <span className="font-mono font-bold uppercase">{text}</span>
    </span>
  );
}
